import { useEffect, useRef } from 'react';

/**
 * Custom DOM event name for discovery section rendering.
 * This event is fired by the Post Office discovery section placement
 * when it finishes loading (with or without content), and is listened to by the app switcher.
 */
export const DISCOVERY_SECTION_RENDERED_EVENT = 'discovery-section-rendered';

/**
 * Custom event interface for discovery section rendered event
 */
export interface DiscoverySectionRenderedEvent extends CustomEvent {
	detail: {
		renderedContent: boolean;
	};
}

/**
 * Function to notify the switcher about discovery section state.
 * Call this from the Post Office placement when the section finishes loading.
 */
export const notifyDiscoverySectionRendered = (renderedContent: boolean): void => {
	// Also dispatch event for currently mounted listeners
	if (typeof globalThis?.document !== 'undefined') {
		const event = new CustomEvent(DISCOVERY_SECTION_RENDERED_EVENT, {
			detail: { renderedContent },
		});
		globalThis.document.dispatchEvent(event);
	}
};

/**
 * Custom hook to listen for discovery section rendered events.
 * This hook sets up and cleans up the event listener automatically.
 */
export const useDiscoverySectionEventListener = (
	callback: (event: DiscoverySectionRenderedEvent) => void,
): void => {
	// Use ref to always point to latest callback without triggering effect re-runs
	const callbackRef = useRef(callback);
	callbackRef.current = callback;

	useEffect(() => {
		const handleEvent = (event: Event) => {
			callbackRef.current(event as DiscoverySectionRenderedEvent);
		};

		globalThis?.document?.addEventListener(DISCOVERY_SECTION_RENDERED_EVENT, handleEvent);

		return () => {
			globalThis?.document?.removeEventListener(DISCOVERY_SECTION_RENDERED_EVENT, handleEvent);
		};
	}, []);
};
